import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronDown, Store, Truck, XCircle } from "lucide-react";
import { Auticko, AutickoLoader, Button, Modal, PageHero } from "../../components";
import {
  CUSTOMER_ORDERS_URL,
  CUSTOMER_ORDER_CANCEL_URL,
} from "../../features/UrlHelper";
import "./MojeObjednavkyPage.scss";

/**
 * Prehlad vlastnych objednaviek zakaznika.
 *
 * Zamerne NEUKAZUJE trasu, kod adresy ani poznamku vodica -- su to interne
 * udaje prevadzky a server ich do odpovede ani nedava.
 *
 * Zrusit sa da len objednavka, ktora je este "Prijata" a na den, ktory sa da
 * objednat (M5). Ci to plati, rozhoduje server: posiela `moznoZrusit` podla
 * rovnakych pravidiel, ake uplatni pri samotnom zruseni.
 */
const STAVY = {
  ACCEPTED: { text: "Prijatá", trieda: "prijata" },
  DELIVERY: { text: "Na ceste", trieda: "na-ceste" },
  DELIVERED: { text: "Doručená", trieda: "dorucena" },
  CANCELLED: { text: "Zrušená", trieda: "zrusena" },
};

/*
 * Cesta objednávky: Prijatá → Na ceste → Doručená, autíčko stojí na kroku,
 * kde objednávka práve je. Kým je "Na ceste", pohupuje sa.
 *
 * Len pri donáške a len kým objednávka nie je vybavená -- pri vyzdvihnutí
 * žiadne autíčko nejde a pri starých doručených by cesta len zaberala miesto
 * (značka v rohu povie to isté).
 *
 * Zákazník realtime udalosti nedostáva (RealtimeContext), takže autíčko
 * sa posunie až po novom načítaní stránky.
 */
const KROKY_CESTY = [
  { stav: "ACCEPTED", text: "Prijatá" },
  { stav: "DELIVERY", text: "Na ceste" },
  { stav: "DELIVERED", text: "Doručená" },
];

const ukazCestu = (o) =>
  o.delivery_type !== "PICKUP" && (o.status === "ACCEPTED" || o.status === "DELIVERY");

function CestaObjednavky({ stav }) {
  const krok = Math.max(0, KROKY_CESTY.findIndex((k) => k.stav === stav));
  return (
    <div className="moje-objednavky__cesta" style={{ "--krok": krok }}>
      <div className="moje-objednavky__cesta-draha" aria-hidden="true">
        <span className="moje-objednavky__cesta-prejdene" />
        <Auticko
          rezim={stav === "DELIVERY" ? "na-mieste" : "stoji"}
          sirka={56}
          className="moje-objednavky__cesta-auto"
        />
      </div>
      <ol className="moje-objednavky__cesta-kroky">
        {KROKY_CESTY.map((k, i) => (
          <li
            key={k.stav}
            className={i === krok ? "moje-objednavky__cesta-krok--aktualny" : i < krok ? "moje-objednavky__cesta-krok--hotovy" : undefined}
            aria-current={i === krok ? "step" : undefined}
          >
            {k.text}
          </li>
        ))}
      </ol>
    </div>
  );
}

const eur = (v) => `${Number(v || 0).toFixed(2)} €`;

// Dnešok podľa prevádzky, nie podľa hodín v prehliadači zákazníka.
const dnesIso = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bratislava" }).format(new Date());

const jeAktivna = (o) => o.status === "ACCEPTED" || o.status === "DELIVERY";

/*
 * Rozdelenie podľa dňa dodania. Server posiela od najnovšej; dnešné aktívne
 * idú hore, nadchádzajúce od najbližšej, minulé ostávajú od najnovšej.
 * Objednávať sa dá aj na ďalšie dni (M5), preto popri "dnes" a "minulé"
 * existujú aj nadchádzajúce.
 */
function rozdel(objednavky, dnes) {
  const dnesne = objednavky.filter((o) => o.delivery_date === dnes);
  return {
    dnesne: [...dnesne.filter(jeAktivna), ...dnesne.filter((o) => !jeAktivna(o))],
    nadchadzajuce: objednavky.filter((o) => o.delivery_date > dnes).reverse(),
    minule: objednavky.filter((o) => o.delivery_date < dnes),
  };
}

const datum = (iso) => {
  if (!iso) return "";
  const [r, m, d] = String(iso).slice(0, 10).split("-");
  return `${Number(d)}. ${Number(m)}. ${r}`;
};

/*
 * Sekcia so zoznamom. `zbalitelna` dostane nadpis ako tlačidlo, ktorým sa
 * zoznam dá skryť -- minulé objednávky sú len história a nemajú vytláčať
 * dnešok. Začína rozbalená.
 */
function Sekcia({ nazov, podnazov, pocet, minula, zbalitelna, children }) {
  const [otvorena, setOtvorena] = useState(true);

  const obsahNadpisu = (
    <>
      {nazov}
      {podnazov && <span className="moje-objednavky__sekcia-podnazov">{podnazov}</span>}
      {pocet > 0 && <span className="moje-objednavky__sekcia-pocet">{pocet}</span>}
    </>
  );

  return (
    <section className={`moje-objednavky__sekcia${minula ? " moje-objednavky__sekcia--minula" : ""}`}>
      <h2 className="moje-objednavky__sekcia-nadpis">
        {zbalitelna ? (
          <button
            type="button"
            className="moje-objednavky__sekcia-prepinac"
            aria-expanded={otvorena}
            onClick={() => setOtvorena((v) => !v)}
          >
            {obsahNadpisu}
            <span className="moje-objednavky__sekcia-akcia">
              {otvorena ? "Skryť" : "Zobraziť"}
              <ChevronDown
                size={16}
                aria-hidden="true"
                className={`moje-objednavky__sekcia-sipka${otvorena ? " moje-objednavky__sekcia-sipka--otvorena" : ""}`}
              />
            </span>
          </button>
        ) : obsahNadpisu}
      </h2>
      {otvorena && children}
    </section>
  );
}

function Zoznam({ objednavky, dnes, naZrusenie }) {
  return (
    <ul className="moje-objednavky__zoznam">
      {objednavky.map((o) => (
        <KartaObjednavky key={o.id} o={o} dnes={dnes} naZrusenie={naZrusenie} />
      ))}
    </ul>
  );
}

/*
 * Dnešná objednávka, ktorá ešte nie je vybavená (Prijatá / Na ceste), je
 * zvýraznená -- to je tá, na ktorú zákazník práve čaká.
 */
function KartaObjednavky({ o, dnes, naZrusenie }) {
  const stav = STAVY[o.status] || { text: o.status, trieda: "prijata" };
  // Rozhoduje server (rovnake pravidla ako pri samotnom zruseni) -- minula
  // alebo po uzavierke prijata objednavka sa zrusit neda, tak sa ani neponukne.
  const daSaZrusit = o.moznoZrusit === true;
  const aktivnaDnes = o.delivery_date === dnes && jeAktivna(o);

  return (
    <li className={`moje-objednavky__polozka${aktivnaDnes ? " moje-objednavky__polozka--aktivna" : ""}`}>
      <div className="moje-objednavky__riadok">
        <div>
          <span className="moje-objednavky__datum">{datum(o.delivery_date)}</span>
          <span className="moje-objednavky__kod">{o.order_code}</span>
        </div>
        <div className="moje-objednavky__hlavicka-vpravo">
          <strong className="moje-objednavky__cena">{eur(o.total_price)}</strong>
          <span className={`moje-objednavky__stav-znacka moje-objednavky__stav-znacka--${stav.trieda}`}>
            {stav.text}
          </span>
        </div>
      </div>

      {ukazCestu(o) && <CestaObjednavky stav={o.status} />}

      <div className="moje-objednavky__sposob">
        {o.delivery_type === "PICKUP"
          ? <><Store size={16} aria-hidden="true" /> Vyzdvihnutie na prevádzke</>
          : <><Truck size={16} aria-hidden="true" /> Donáška{o.address ? ` — ${o.address}` : ""}</>}
      </div>

      {o.items?.length > 0 && (
        <ul className="moje-objednavky__jedla">
          {o.items.map((p, i) => (
            <li key={`${o.id}-${i}`} className="moje-objednavky__jedlo">
              <span className="moje-objednavky__jedlo-pocet">{p.quantity}×</span>
              {p.menu_code && <span className="moje-objednavky__jedlo-kod">{p.menu_code}</span>}
              <span className="moje-objednavky__jedlo-nazov">{p.name}</span>
            </li>
          ))}
        </ul>
      )}

      {o.dispatcher_note && (
        <p className="moje-objednavky__poznamka">Poznámka: {o.dispatcher_note}</p>
      )}

      {o.status === "CANCELLED" && o.cancel_reason && (
        <p className="moje-objednavky__dovod">Dôvod zrušenia: {o.cancel_reason}</p>
      )}

      {daSaZrusit && (
        <div className="moje-objednavky__spodok">
          <button
            type="button"
            className="moje-objednavky__zrus"
            onClick={() => naZrusenie(o)}
          >
            <XCircle size={15} aria-hidden="true" /> Zrušiť
          </button>
        </div>
      )}
    </li>
  );
}

function MojeObjednavkyPage() {
  const [objednavky, setObjednavky] = useState([]);
  const [nacitava, setNacitava] = useState(true);
  const [chyba, setChyba] = useState("");
  const [rusi, setRusi] = useState(null);
  const [rusenieBezi, setRusenieBezi] = useState(false);

  const nacitaj = useCallback(async () => {
    setNacitava(true);
    try {
      const { data } = await axios.get(CUSTOMER_ORDERS_URL);
      setObjednavky(Array.isArray(data) ? data : []);
      setChyba("");
    } catch (err) {
      setChyba(err?.response?.data?.message || "Objednávky sa nepodarilo načítať.");
    } finally {
      setNacitava(false);
    }
  }, []);

  useEffect(() => { nacitaj(); }, [nacitaj]);

  const dnes = dnesIso();
  const { dnesne, nadchadzajuce, minule } = rozdel(objednavky, dnes);

  async function zrus() {
    if (!rusi) return;
    setRusenieBezi(true);
    try {
      await axios.post(CUSTOMER_ORDER_CANCEL_URL(rusi.id));
      setRusi(null);
      await nacitaj();
    } catch (err) {
      setChyba(err?.response?.data?.message || "Objednávku sa nepodarilo zrušiť.");
      setRusi(null);
    } finally {
      setRusenieBezi(false);
    }
  }

  return (
    <main className="moje-objednavky">
      <PageHero
        eyebrow="Vaše konto"
        title="Moje"
        zvyraznene="objednávky"
        lead="Čo ste si objednali, v akom je to stave a koľko to stálo."
        akcie={
          <Link to="/objednavka" className="moje-objednavky__cta">Objednať znova</Link>
        }
      />

      <div className="moje-objednavky__obsah">
      {chyba && <p className="moje-objednavky__chyba" role="alert">{chyba}</p>}

      {nacitava && <AutickoLoader text="Načítavam objednávky…" />}

      {!nacitava && objednavky.length === 0 && (
        <div className="moje-objednavky__prazdne">
          <Auticko sirka={96} />
          <p>Zatiaľ ste si nič neobjednali.</p>
          <Link to="/objednavka" className="moje-objednavky__odkaz">Pozrieť dnešné menu</Link>
        </div>
      )}

      {objednavky.length > 0 && (
        <>
          <Sekcia nazov="Dnes" podnazov={datum(dnes)} pocet={dnesne.length}>
            {dnesne.length === 0
              ? <p className="moje-objednavky__sekcia-prazdna">Na dnes nemáte žiadnu objednávku.</p>
              : <Zoznam objednavky={dnesne} dnes={dnes} naZrusenie={setRusi} />}
          </Sekcia>

          {nadchadzajuce.length > 0 && (
            <Sekcia nazov="Nadchádzajúce" pocet={nadchadzajuce.length}>
              <Zoznam objednavky={nadchadzajuce} dnes={dnes} naZrusenie={setRusi} />
            </Sekcia>
          )}

          {minule.length > 0 && (
            <Sekcia nazov="Minulé" pocet={minule.length} minula zbalitelna>
              <Zoznam objednavky={minule} dnes={dnes} naZrusenie={setRusi} />
            </Sekcia>
          )}
        </>
      )}
      </div>

      <Modal
        isOpen={rusi !== null}
        onClose={() => setRusi(null)}
        title="Zrušiť objednávku?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRusi(null)} disabled={rusenieBezi}>
              Späť
            </Button>
            <Button variant="primary" onClick={zrus} disabled={rusenieBezi}>
              {rusenieBezi ? "Ruším..." : "Áno, zrušiť"}
            </Button>
          </>
        }
      >
        <p>
          Naozaj chcete zrušiť objednávku {rusi?.order_code} na {datum(rusi?.delivery_date)}?
          Táto akcia sa nedá vrátiť späť.
        </p>
      </Modal>
    </main>
  );
}

export default MojeObjednavkyPage;
